// Floating feedback entry point, rendered on every screen of the embedded
// app (including onboarding — early-install bugs are exactly the reports the
// open beta needs). A fixed bottom-right button opens a small Polaris modal:
// pick a type, write the message, optionally leave a reply-to address.
// Submissions POST /api/feedback; the backend snapshots plan + destination
// context so the merchant never has to describe their setup.

import { useState } from "react";
import {
  Modal,
  BlockStack,
  ChoiceList,
  TextField,
  Text,
  Banner,
} from "@shopify/polaris";
import { useAppBridge } from "@shopify/app-bridge-react";
import { apiFetch } from "./api";

type FeedbackType = "bug" | "idea" | "other";

// The launcher is a custom pill rather than a Polaris <Button>: it needs to be
// visually loud (the open beta lives on merchants actually reporting things),
// and Polaris buttons don't do fixed-position FABs, brand color, or this size.
// Hover/focus/active need real CSS states, so the styles ship as a tiny
// scoped stylesheet next to the button instead of inline style props.
const FAB_CSS = `
.dc-feedback-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 400;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border: none;
  border-radius: 999px;
  background: #1f7a66;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(4, 44, 35, 0.35);
  transition: background 120ms ease, transform 120ms ease, box-shadow 120ms ease;
}
.dc-feedback-fab:hover {
  background: #175f4f;
  transform: translateY(-1px);
  box-shadow: 0 8px 22px rgba(4, 44, 35, 0.4);
}
.dc-feedback-fab:active {
  transform: translateY(0);
}
.dc-feedback-fab:focus-visible {
  outline: 3px solid #9adfcb;
  outline-offset: 2px;
}
@media (max-width: 640px) {
  .dc-feedback-fab {
    bottom: 16px;
    right: 16px;
    padding: 14px 20px;
    font-size: 15px;
  }
}
`;

export function FeedbackWidget() {
  const shopify = useAppBridge();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>("bug");
  const [message, setMessage] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (sending) return;
    setOpen(false);
    setError(null);
  };

  const submit = async () => {
    if (!message.trim()) {
      setError("Write a sentence or two first — the message is the report.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await apiFetch(shopify, "/api/feedback", {
        method: "POST",
        body: JSON.stringify({
          type,
          message: message.trim(),
          contactEmail: contactEmail.trim(),
          page: window.location.pathname,
        }),
      });
      setSending(false);
      setOpen(false);
      setMessage("");
      setContactEmail("");
      try {
        // App Bridge v4 toast; if unavailable, the closed modal is confirmation.
        shopify.toast.show("Sent — thank you. We read every one.");
      } catch {
        // no-op
      }
    } catch (err) {
      setSending(false);
      setError(
        (err as Error).message || "Sending failed. Please try again in a moment.",
      );
    }
  };

  return (
    <>
      <style>{FAB_CSS}</style>
      <button
        type="button"
        className="dc-feedback-fab"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <svg width="22" height="22" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 2C5.58 2 2 4.91 2 8.5c0 1.83.94 3.48 2.45 4.66-.1.95-.53 2.09-1.62 3.18-.23.23-.07.62.25.6 2.02-.08 3.5-.92 4.42-1.66.79.19 1.63.3 2.5.3 4.42 0 8-2.99 8-6.58S14.42 2 10 2z"
            fill="currentColor"
          />
        </svg>
        Feedback
      </button>
      <Modal
        open={open}
        onClose={close}
        title="Send feedback"
        primaryAction={{ content: "Send", onAction: () => void submit(), loading: sending }}
        secondaryActions={[{ content: "Cancel", onAction: close, disabled: sending }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" tone="subdued">
              Goes straight to the person who built Datacrow. Reports arrive
              with your plan and destination setup attached, so you don&rsquo;t
              need to describe your configuration.
            </Text>
            <ChoiceList
              title="What kind?"
              choices={[
                { label: "Something's broken", value: "bug" },
                { label: "An idea or request", value: "idea" },
                { label: "General feedback", value: "other" },
              ]}
              selected={[type]}
              onChange={(selected) => setType((selected[0] as FeedbackType) ?? "bug")}
            />
            <TextField
              label={
                type === "bug"
                  ? "What happened, and what did you expect instead?"
                  : "What's on your mind?"
              }
              value={message}
              onChange={setMessage}
              multiline={5}
              autoComplete="off"
              maxLength={5000}
              showCharacterCount={message.length > 4500}
            />
            <TextField
              label="Email for a reply (optional)"
              type="email"
              value={contactEmail}
              onChange={setContactEmail}
              autoComplete="email"
              helpText="Only used to answer this message."
            />
            {error ? (
              <Banner tone="critical">
                <p>{error}</p>
              </Banner>
            ) : null}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
